import { createContext, ReactNode, useContext, useMemo, useState } from 'react';

interface ModalContextValue {
  isOpen: boolean;
  setOpen: (open: boolean) => void;
  disableOverlay?: boolean;
  disableScroll?: boolean;
}

const ModalContext = createContext<ModalContextValue | undefined>(undefined);

export const useModal = () => {
  const context = useContext(ModalContext);
  if (!context) {
    throw new Error('useModal must be used within a ModalProvider');
  }
  return context;
};

export interface ModalProviderProps {
  children: ReactNode;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  disableOverlay?: boolean;
  disableScroll?: boolean;
}

export function ModalProvider({
  children,
  open,
  onOpenChange,
  disableOverlay = false,
  disableScroll = true,
}: ModalProviderProps) {
  const [internalOpen, setInternalOpen] = useState(false);

  const isControlled = open !== undefined;
  const isOpen = isControlled ? open : internalOpen;

  const value = useMemo(
    () => ({
      isOpen,
      setOpen: onOpenChange || setInternalOpen,
      disableOverlay,
      disableScroll,
    }),
    [isOpen, isControlled, onOpenChange],
  );

  return (
    <ModalContext.Provider value={value}>{children}</ModalContext.Provider>
  );
}
